import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, map, catchError, timeout } from 'rxjs';
import { StorageService } from '../services/storage';
import { MenuService } from '../services/menu';
import { AuthService } from '../services/auth';
import { ToastService } from '../services/toast';
import { isJwtExpired } from '../utils/jwt';

/** The no-access page must always be reachable once authenticated, otherwise the redirect
 *  below would loop. Every other route requires the matching menu in the user's set. */
const FORBIDDEN = '/forbidden';
// Reachable by any authenticated user regardless of their menu set (no menu entry of their own).
// '/partner-kyc' is here so a not-yet-approved partner (whose menu may not list it) can still
// reach the KYC page the kycGuard parks them on.
// '/member-onboarding' is allowed here so the page is reachable before an admin adds a
// matching menu entry via Menu Master. Once a menu item exists, this entry can be removed
// to bring the page back under normal menu-based access control.
// '/communication-config' and '/message-templates' are Admin-only screens (the backend
// controllers are [Authorize(Roles="Admin")]) listed here for the same reason — reachable
// for testing until an admin adds their Menu Master entries, then they can be removed.
const ALWAYS_ALLOWED = new Set<string>([
  FORBIDDEN,
  // '/dashboard' is a pure role dispatcher (dashboardRedirectGuard) — always reachable
  // so every "go home" navigation resolves even before a menu row exists.
  '/dashboard',
  '/change-password',
  '/partner-kyc',
  '/member-onboarding',
  '/communication-config',
  '/message-templates',
  // Unified messaging module (Rules | Templates | Providers | Outbox). Admin-only on the
  // backend ([Authorize(Roles="Admin")] on MessageRuleController); listed here so it stays
  // reachable until seed_messaging_rules_menu.sql has been run.
  '/messaging',
  // Payments module — reachable until an admin adds Menu Master entries (the backend
  // [Authorize(Roles="Admin")] still gates the admin masters). Collect is any-authed
  // (partner self-service); '/payments/list' is ADMIN-ONLY — the ADMIN_ONLY set below is
  // checked first and hard-blocks partners, so its entry here only opens it for admins
  // who don't have a menu row yet.
  '/payments/gateways',
  '/payments/integrations',
  '/payments/types',
  '/payments/list',
  // Receipts — finder is any-authed (partner self-service). The success page (/payments/success/:id)
  // is matched by prefix below since it carries a dynamic segment.
  '/payments/receipts',
  // Certificate of Insurance — finder is any-authed; the per-member certificate page
  // (/policies/certificate/:memberId) is matched by prefix below (dynamic segment).
  '/policies/certificates',
  // Global Master — Admin-only on the backend ([Authorize(Roles="Admin")]); reachable here until
  // an admin adds its Menu Master entry (assigned to the Admin role), then this can be removed.
  '/masters/global',
  // Receipt Settings — Admin-only on the backend ([Authorize(Roles="Admin")]); reachable until an
  // admin adds its Menu Master entry (assigned to the Admin role), then this can be removed.
  '/masters/receipt-settings',
  // COI Header — Admin-only on the backend ([Authorize(Roles="Admin")]); reachable until an admin
  // adds its Menu Master entry (assigned to the Admin role), then this can be removed.
  '/masters/coi-header',
  // Hospicash Settings — Admin-only on the backend; reachable until its Menu Master entry exists.
  '/masters/hospicash-setting',
  // Test Mode console + Go-Live readiness — Admin-only on the backend and hard-blocked below;
  // listed here so they are reachable before their Menu Master entries exist.
  '/masters/test-mode',
  '/masters/go-live',
  // Message Catalog — Admin-only reference page; reachable until its Menu Master entry exists.
  '/message-catalog',
  // Product Master — single page for master policies + premium rules + benefit breakup.
  // The old /master-policies and /premium-rules routes redirect here, so it must stay
  // reachable for anyone whose menu carries those entries.
  '/product-master',
  // Zone / State / Regional-Office masters — Admin-only on the backend ([Authorize(Roles="Admin")]);
  // reachable until an admin adds their Menu Master entries, then these can be removed.
  '/zones',
  '/states',
  '/regional-offices',
  // Client Management module — Admin-only on the backend ([Authorize(Roles="Admin")]);
  // reachable until an admin adds the Menu Master entries, then these can be removed.
  '/clients',
  '/client-messaging',
  '/client-callbacks',
  // Admin Dashboard + Report Scheduler + Admin Users + All Members — hard-blocked for
  // partners via ADMIN_ONLY below; listed here so admins can reach them before their
  // Menu Master entries exist.
  '/admin/dashboard',
  '/admin/report-scheduler',
  '/admin-users',
  '/members',
  '/terms-conditions',
  // App Notifications — Admin-only on the backend (PartnerAppAdmin controller); reachable here
  // even though its Menu Master row is already seeded, so admins whose menu hasn't refreshed yet
  // can still open it. Can be removed once every admin's menu carries the entry.
  '/app-notifications',
  // Partner Dashboard + Records + Member Lookup — reachable until an admin adds their Menu Master
  // entries. All are partner-scoped on the backend (partner derived from the JWT), so any authed
  // user is fine.
  '/partner-dashboard',
  '/partner-records',
  '/member-lookup',
  // Where a REDIRECT gateway returns the customer. It has no Menu Master entry and never will —
  // nobody navigates to it, the gateway sends them. Without this a partner completed a Generali
  // payment and landed on the No Access page instead of their receipt.
  '/payments/result',
]);

// Routes with a dynamic segment can't be matched by exact membership above; allow by prefix.
// '/clients/' covers the client detail page (/clients/:id) the same way.
const ALWAYS_ALLOWED_PREFIXES = [
  '/payments/collect/',
  '/payments/success/',
  // Payment Details (/payments/detail/:id) — any authed user; the backend scopes it
  // (admin → any; partner → only their own member's payment), so it's NOT admin-only.
  '/payments/detail/',
  '/policies/certificate/',
  '/clients/',
  // Partner detail (/partners/:id) — same treatment as the client detail page.
  '/partners/',
];

// Admin-only routes: a partner (role User) is never allowed here, even if a menu entry or the
// ALWAYS_ALLOWED list would otherwise permit it. The "All Payments" master list is admins only —
// partners collect via /payments/collect/:memberId but must not see the full payments ledger.
const ADMIN_ONLY = new Set<string>([
  '/payments/list',
  '/admin/dashboard',
  '/admin/report-scheduler',
  // Login-account management (create admins, reset passwords) is strictly admin territory.
  '/admin-users',
  '/members',
  '/terms-conditions',
  // Partner-app push/issues console — backend is Admin-role gated; a partner
  // opening it would only get a wall of 403 toasts.
  '/app-notifications',
  // Toggling test mode / purging test data is an admin action; the backend is Admin-role gated.
  '/masters/test-mode',
  '/masters/go-live',
]);

// Partner-only routes: these screens are scoped to the PARTNER in the JWT, so an admin opening
// them sees empty/broken data (and has richer admin equivalents anyway). Admins are silently
// sent to their own dashboard instead — an admin must never land on the partner dashboard.
const PARTNER_ONLY = new Set<string>([
  '/partner-dashboard',
  '/partner-records',
  '/member-lookup',
  // The partner KYC form is for partners; admins review KYC on /partner-kyc-review.
  '/partner-kyc',
]);

// A hung/slow API must not freeze navigation forever (the user would stay parked on /login
// after a successful sign-in with no feedback). After this long we give up waiting and fall
// back to the decide()/fail-open paths below.
const MENU_LOAD_TIMEOUT_MS = 10_000;

export const authGuard: CanActivateFn = (_route, state): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const storage = inject(StorageService);
  const menu = inject(MenuService);
  const auth = inject(AuthService);
  const toast = inject(ToastService);

  // On the server (SSR / prerender) localStorage doesn't exist, so the token is never
  // visible there. Skip the check and let the browser re-run the guard after hydration.
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return true;
  }

  const token = storage.getLocal('token');
  if (!token) {
    return router.parseUrl('/login');
  }

  // Expired JWT with no refresh token = a definitively dead session. The auth
  // interceptor only reacts to a live 401, and the shell watchdog only runs once
  // Layout is mounted — so a page reload or a same-tab navigation that fires no
  // request would otherwise admit a broken session and sit on stale content.
  // Reject it here so expiry lands the user on /login deterministically. (An
  // expired token WITH a refresh token is let through: the interceptor/watchdog
  // silently rotates it, or ends the session if that refresh also fails.)
  if (isJwtExpired(token) && !storage.getLocal('refreshToken')) {
    storage.clearAll();
    return router.parseUrl('/login');
  }

  const url = state.url.split('?')[0];

  const decide = (): boolean | UrlTree => {
    // Hard block: admin-only routes are off-limits to non-admins regardless of any allow-list/menu.
    if (ADMIN_ONLY.has(url) && !auth.isAdmin()) {
      return router.parseUrl(FORBIDDEN);
    }
    // Admins never land on partner-scoped screens — send them to their own dashboard.
    if (PARTNER_ONLY.has(url) && auth.isAdmin()) {
      return router.parseUrl('/admin/dashboard');
    }
    if (
      ALWAYS_ALLOWED.has(url) ||
      ALWAYS_ALLOWED_PREFIXES.some((p) => url.startsWith(p)) ||
      menu.isRouteAllowed(url)
    )
      return true;
    // Route not allowed → say so on the "No Access" page (a silent bounce to some other
    // page just looks like a broken link). It offers "Go to my dashboard" to recover.
    return router.parseUrl(FORBIDDEN);
  };

  // Allowed-routes already loaded (Option A: fast, local membership check).
  if (menu.loaded()) {
    return decide();
  }

  // First protected navigation after a refresh: load the menu once, then decide.
  // The timeout keeps a hung API from freezing navigation forever (stuck on /login after a
  // successful sign-in) — on expiry we fall through to the same fail-open path as a 5xx.
  return menu.loadMyMenu().pipe(
    timeout(MENU_LOAD_TIMEOUT_MS),
    map(() => decide()),
    catchError((err) => {
      // A 401 is a dead session, not an unreachable API. Failing open here admitted the user
      // into the app shell with an expired token, where every subsequent call 401s and the
      // screen fills with error toasts. End it cleanly instead.
      if (err?.status === 401) {
        storage.clearAll();
        return of(router.parseUrl('/login'));
      }
      // The auth interceptor may have ended the session while we waited (its token refresh
      // failed): the error reaching us then isn't a 401, but the session is gone all the
      // same. Failing open would render the app shell with no session behind it.
      if (!storage.getLocal('token')) {
        return of(router.parseUrl('/login'));
      }
      // Anything else (timeout / network / 5xx) still fails open — the backend [Authorize]
      // remains the real gate — but say so instead of silently landing the user in an app
      // with no sidebar. Only the hard role blocks still apply (they don't need the menu);
      // the menu-based route check is skipped because the menu never arrived.
      toast.warning('Could not load your menu. Some pages may be unavailable — please refresh.');
      if (ADMIN_ONLY.has(url) && !auth.isAdmin()) return of(router.parseUrl(FORBIDDEN));
      if (PARTNER_ONLY.has(url) && auth.isAdmin()) return of(router.parseUrl('/admin/dashboard'));
      return of(true);
    }),
  );
};
